import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const ACTION_LABEL = {
  reply: ["Ответили", "bg-green-100 text-green-700"],
  skip: ["Пропущен", "bg-gray-100 text-gray-600"],
  escalate: ["Вам на разбор", "bg-amber-100 text-amber-700"],
  failed: ["Ошибка", "bg-red-100 text-red-600"],
};

export default function IgCommentsPanel({ API }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/zernio/comments/settings`);
      setCfg(r.data);
    } catch { setCfg({ enabled: false, mode: "public_dm", instructions: "", log: [] }); }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/zernio/comments/settings`, patch);
      setCfg(r.data);
      toast.success("Сохранено");
    } catch { toast.error("Не удалось сохранить"); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  return (
    <div className="mt-4 pt-4 border-t border-pink-200" data-testid="ig-comments-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h4 className="font-bold text-black text-base">💬 Ответы на комментарии</h4>
          <p className="text-xs text-gray-600">
            AI отвечает на комментарии под вашими постами и Reels от имени салона — без представления «я ИИ».
            Негатив и жалобы не комментирует, а присылает вам.
          </p>
        </div>
        <button type="button" data-testid="ig-comments-toggle" disabled={saving}
          onClick={() => save({ enabled: !cfg.enabled })}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 shrink-0 transition-colors ${
            cfg.enabled ? "border-green-600 bg-green-600 text-white" : "border-gray-400 bg-white text-gray-600"}`}>
          {cfg.enabled ? "🟢 Включено" : "⚪ Выключено"}
        </button>
      </div>
      {cfg.enabled && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ig-comments-mode">
            {[
              { id: "public_dm", title: "Коротко в комментарии + детали в Direct", desc: "Рекомендуем: цены и запись уходят в личку, публично — «написали вам в Direct 💌»" },
              { id: "public", title: "Полный ответ в комментарии", desc: "AI отвечает прямо под постом (цены могут быть видны всем)" },
            ].map((m) => (
              <button key={m.id} type="button" data-testid={`ig-comments-mode-${m.id}`}
                onClick={() => cfg.mode !== m.id && save({ mode: m.id })}
                className={`text-left p-3 rounded-xl border-2 transition-colors ${cfg.mode === m.id ? "border-black bg-black text-white" : "border-gray-200 bg-white hover:border-gray-400"}`}>
                <div className="text-sm font-bold">{m.title}</div>
                <div className={`text-xs mt-1 ${cfg.mode === m.id ? "text-gray-200" : "text-gray-500"}`}>{m.desc}</div>
              </button>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Ваши правила для ответов (необязательно)</label>
            <textarea data-testid="ig-comments-instructions" rows={2}
              value={cfg.instructions}
              onChange={(e) => setCfg({ ...cfg, instructions: e.target.value })}
              onBlur={() => save({ instructions: cfg.instructions })}
              placeholder="Например: не называй цены публично; всегда приглашай в Direct; обращайся на «вы»"
              className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none" />
          </div>
          <p className="text-[11px] text-gray-500">
            ⚖️ Совет: добавьте в шапку профиля строку «Отвечаем с помощью AI-ассистента» — это закрывает требование прозрачности (EU AI Act) без упоминания ИИ в каждом ответе.
          </p>
          <div data-testid="ig-comments-log">
            <div className="text-xs font-semibold text-gray-700 mb-1">Последние комментарии</div>
            {cfg.log.length === 0 ? (
              <p className="text-xs text-gray-500">Пока пусто — появится после первого комментария под вашим постом.</p>
            ) : (
              <div className="bg-white border border-gray-200 rounded-md divide-y max-h-64 overflow-y-auto">
                {cfg.log.map((l) => {
                  const [label, cls] = ACTION_LABEL[l.action] || ACTION_LABEL.skip;
                  return (
                    <div key={l.id} className="px-3 py-2 text-xs" data-testid={`ig-comment-row-${l.comment_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">@{l.author || "user"}</span>
                        <span className={`px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                        <span className="text-gray-400 ml-auto">{new Date(l.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-gray-700 mt-0.5">«{l.text}»</div>
                      {l.public_reply && <div className="text-green-700 mt-0.5">↳ {l.public_reply}</div>}
                      {l.dm_text && <div className="text-purple-700 mt-0.5">✉️ Direct: {l.dm_text}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
